import { Button, Descriptions, Table, Tag, Typography } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { chemblTargetExternalLink, keggTargetExternalLink, uniprotTargetExternalLink } from '../hooks/TableLink';
import {
    chineseDrugLink,
    diseaseLink,
    ingredientLink,
    keggPathExternalLink,
    plantDetailLink,
    uniprotDetailLink,
} from '../hooks/TableLink';

import _ from 'lodash';
import useFetch from '../hooks/fetcher';
import { ColumnsType, SortOrder } from 'antd/lib/table/interface';
const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';
const DATA_SERVICE_BASE_URL = process.env.REACT_APP_DATA_SERVICE_BASE_URL || '';

const { Title } = Typography;
const { Item } = Descriptions;
const { Text } = Typography;

// backend response data structure
type ipath3Type = {
    chembl_id: string;
    color: string;
    width: string;
};

const ipath3ResponseProcessor = (r: ipath3Type[]): string[] => r.map((e) => `${e.chembl_id} ${e.color} ${e.width}`);

const tAreaDivider = '&#13;&#10;';

type RecipeDetailType = {
    recipes_id: string[];
    chinese_character: string;
    pinyin: string;
    english_name: string;
    category: string;
    patterns: string;
    actions: string;
    pulse_patterns: string;
    tongue_appearance: string;
    channels: string;
    herb_drug_interactions: string;
    preparation_notes: string;
    notes: string;
    originally_appeared_in: string;
};

type RelatedChineseDrugInfoType = {
    botanical_id: string;
    chinese_id: string;
    botanical_name: string;
    chinese_pharmaceutical_name: string;
    pinyin: string;
};

type RelatedIngredientsType = {
    ingredients_id: string;
    compound_name: string;
    molecular_formula: string;
    mw: string;
    xlogp: string;
    hba: string;
    hbd: string;
    tpsa: string;
    rotatable_bond: string;
    lipinski_failure: string;
    atcs: string;
};

type IngredientsTargetsType = {
    ingredients_id: string;
    protein: string;
    gene: string;
    chembl_id: string;
    uniprot_entry: string;
    kegg_id: string;
    pharmacological_action: string;
    druggable: string;
};

type RelatedDiseasesType = {
    id: string;
    level4: string;
    level4_description: string;
    targets: string;
};

type RelatedPathwaysType = {
    pathway_id: string;
    pathway_name: string;
    count_current_druggable_targets: number;
    p21: number;
    e21: number;
};

type RecipeDetailResponseType = {
    detail: RecipeDetailType[];
    chinese_pharmaceutical_name: { chinese_pharmaceutical_name: string }[];
    related_chinese_drug_info: RelatedChineseDrugInfoType[];
    ingredients_targets_association: IngredientsTargetsType[];
    related_diseases: RelatedDiseasesType[];
    related_pathways: RelatedPathwaysType[];
    related_ingredients: RelatedIngredientsType[];
};
const uniprotTags = (uniprotList?: string) => {
    if (_.isNil(uniprotList)) {
        return null;
    }
    return uniprotList.split(',').map((item: string, key: number) => {
        return (
            <Link to={`/target/${item}`} key={key}>
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </Link>
        );
    });
};
const atcTags = (atcList?: string) => {
    if (_.isNil(atcList)) {
        return 'NA';
    }
    return atcList.split(',').map((item: string, key: number) => {
        return (
            <a
                href={`https://www.whocc.no/atc_ddd_index/?code=+${item}+&showdescription=no`}
                target="_blank"
                rel="noopener noreferrer"
                key={key}
            >
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </a>
        );
    });
};
const columnsRelatedCDI = [
    {
        title: 'Accepted Botanical Name',
        dataIndex: 'botanical_name',
        key: 'botanical_name',
        render: plantDetailLink,
    },
    {
        title: 'Chinese Pharmaceutical Name',
        dataIndex: 'chinese_pharmaceutical_name',
        key: 'chinese_pharmaceutical_name',
        render: chineseDrugLink,
    },
    {
        title: 'Transliteration',
        dataIndex: 'pinyin',
        key: 'pinyin',
        render: chineseDrugLink,
    },
];

const columnRi = [
    {
        title: 'Ingredients ID',
        key: 'ingredients_id',
        dataIndex: 'ingredients_id',
        // eslint-disable-next-line react/display-name
        render: ingredientLink,
    },
    { title: 'Ingredients Name', key: 'compound_name', dataIndex: 'compound_name' },
    { title: 'Formula', key: 'molecular_formula', dataIndex: 'molecular_formula' },
    {
        title: 'MW',
        key: 'mw',
        dataIndex: 'mw',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number => parseInt(a.mw) - parseInt(b.mw),
    },
    {
        title: 'XLogP',
        key: 'xlogp',
        dataIndex: 'xlogp',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number => parseInt(a.xlogp) - parseInt(b.xlogp),
    },
    {
        title: 'HBA',
        key: 'hba',
        dataIndex: 'hba',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number => parseInt(a.hba) - parseInt(b.hba),
    },
    {
        title: 'HBD',
        key: 'hbd',
        dataIndex: 'hbd',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number => parseInt(a.hbd) - parseInt(b.hbd),
    },
    {
        title: 'TPSA',
        key: 'tpsa',
        dataIndex: 'tpsa',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number => parseInt(a.tpsa) - parseInt(b.tpsa),
    },
    {
        title: 'Rotatable Bond',
        key: 'rotatable_bond',
        dataIndex: 'rotatable_bond',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number =>
            parseInt(a.rotatable_bond) - parseInt(b.rotatable_bond),
    },
    {
        title: 'Lipinski Failure',
        key: 'lipinski_failure',
        dataIndex: 'lipinski_failure',
        sorter: (a: RelatedIngredientsType, b: RelatedIngredientsType): number =>
            parseInt(a.lipinski_failure) - parseInt(b.lipinski_failure),
    },
    { title: 'ATC Codes', key: 'atcs', dataIndex: 'atcs', render: atcTags },
];

const columnsIngredientTarget = [
    { title: 'Ingredients ID', dataIndex: 'ingredients_id', key: 'ingredients_id', render: ingredientLink },
    { title: 'Targets', dataIndex: 'protein', key: 'protein', render: uniprotDetailLink },
    { title: 'Gene', dataIndex: 'gene', key: 'gene' },
    { title: 'ChEMBL ID', dataIndex: 'chembl_id', key: 'chembl_id', render: chemblTargetExternalLink },
    { title: 'UNIPROT ID', dataIndex: 'uniprot_entry', key: 'uniprot_entry', render: uniprotTargetExternalLink },
    { title: 'KEGG ID', dataIndex: 'kegg_id', key: 'kegg_id', render: keggTargetExternalLink },
    { title: 'Pharmacological Action', dataIndex: 'pharmacological_action', key: 'pharmacological_action' },
    { title: 'Druggable', dataIndex: 'druggable', key: 'druggable' },
];

const columnsDiseases = [
    {
        title: 'Disease Code',
        key: 'level4',
        dataIndex: 'level4',
        render: diseaseLink,
    },
    {
        title: 'Disease Annotation',
        key: 'level4_description',
        dataIndex: 'level4_description',
    },
    {
        title: 'Targets',
        key: 'targets',
        dataIndex: 'targets',
        width: 350,
        render: uniprotTags,
    },
];

const columnsPathways: ColumnsType<any> = [
    /*{ title: 'KEGG Pathway', dataIndex: 'kegg_path_id', key: 'kegg_path_id', render: keggPathExternalLink },
    { title: 'Pathway name', dataIndex: 'pathway_name', key: 'pathway_name' },
    { title: 'Targets Uniprot', dataIndex: 'uniprot_entry', key: 'uniprot_entry' },
    { title: 'Targets KEGG', dataIndex: 'kegg_id', key: 'kegg_id' }, */
    { title: 'KEGG Pathway', dataIndex: 'pathway_id', key: 'pathway_id', render: keggPathExternalLink },
    { title: 'Pathway Name', dataIndex: 'pathway_name', key: 'pathway_name' },
    {
        title: 'Count Druggable Targets',
        dataIndex: 'count_current_druggable_targets',
        key: 'count_current_druggable_targets',
        sorter: (a: RelatedPathwaysType, b: RelatedPathwaysType) =>
            a.count_current_druggable_targets - b.count_current_druggable_targets,
    },
    {
        title: 'PValue',
        dataIndex: 'p21',
        key: 'p21',
        sorter: (a: RelatedPathwaysType, b: RelatedPathwaysType) => a.p21 - b.p21,
        defaultSortOrder: 'ascend',
    },
    {
        title: 'EValue',
        dataIndex: 'e21',
        key: 'e21',
        sorter: (a: RelatedPathwaysType, b: RelatedPathwaysType) => a.e21 - b.e21,
    },
];

const PageRecipeDetail: React.FC = () => {
    const { name: searchName, rid: recipeId } = useParams<any>();
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const [RecipeIpath3Param, setRecipeIpath3Param] = useState<string[]>([]);

    const [result, setResult] = useState<RecipeDetailResponseType>();
    const history = useHistory();
    const goToDiseaseDetail = (keyword: string): void => {
        history.push(`/disease/detail/${keyword}`);
    };
    const recipeNumericId = recipeId?.replace('Recipes_', '');

    useEffect(() => {
        get('/api/recipe_detail.php', { recipes_id: recipeId }).then((r) => setResult(r));
        get('/api/recipe_ipath3_param.php', { recipes_id: recipeId }).then((r) =>
            setRecipeIpath3Param(ipath3ResponseProcessor(r)),
        );
    }, []);
    const d = result?.detail[0];
    return (
        <div>
            <Title level={3}>
                Recipe: {d?.chinese_character}({d?.pinyin})
            </Title>
            <Title level={4}>Recipe Details</Title>
            <Descriptions
                // title={<Title level={3}>Recipe Detail</Title>}
                bordered
                colon
                column={1}
                size="middle"
                style={{ marginBottom: '3vh' }}
            >
                <Item label="Recipes ID">{d?.recipes_id}</Item>
                <Item label="Chinese Character">{d?.chinese_character}</Item>
                <Item label="Transliteration (Pinyin)">{d?.pinyin}</Item>
                <Item label="English Name">{d?.english_name}</Item>
                <Item label="Category">{d?.category}</Item>
                <Item label="Patterns">{d?.patterns}</Item>
                <Item label="Actions">{d?.actions}</Item>
                <Item label="Pulse Patterns">{d?.pulse_patterns}</Item>
                <Item label="Tongue Appearance">{d?.tongue_appearance}</Item>
                <Item label="Channels">{d?.channels}</Item>
                <Item label="Herb Drug Interactions">{d?.herb_drug_interactions}</Item>
                <Item label="Preparation Notes">{d?.preparation_notes}</Item>
                <Item label="Notes">{d?.notes}</Item>
                <Item label="Originally Appeared In">{d?.originally_appeared_in}</Item>
                <Item label="Chinese Pharmaceutical Name">
                    {result?.chinese_pharmaceutical_name.map((e) => e.chinese_pharmaceutical_name).join(' / ')}
                </Item>
            </Descriptions>
            <Title level={4}>KEGG Metabolic Pathways</Title>
            <Text>
                <li>green lines: targets</li>
                <li>blue lines: targets with pharmacological action</li>
                <li>red points: ingredients</li>
                <li>violet points: ingredients with ATC code</li>
                Click image to view result in iPath3 interactively. The ingredients and targets information of this
                recipe are stored in the element selection box in the iPath3 website. When you select any map in the
                iPath3 and submit our data, you can see the corresponding information displayed in the map.
            </Text>
            <br />
            <br />
            <Item>
                <form target="_blank" method="post" action="https://pathways.embl.de/ipath3.cgi">
                    <button type="submit" style={{ cursor: 'pointer', border: 'none' }}>
                        <img
                            src={DATA_SERVICE_BASE_URL + `/results_2021_recipes_ipath_img/${recipeNumericId}.svg`}
                            title="click image to view result in iPath3 interactively"
                            style={{ width: '100%' }}
                        />
                    </button>
                    <div style={{ opacity: 0 }}>
                        <textarea
                            name="selection"
                            value={RecipeIpath3Param.reduce(
                                (acc, cur) => (acc === '' ? cur : acc + tAreaDivider + cur),
                                '',
                            )}
                        ></textarea>
                        <input name="default_opacity" type="text" value="1"></input>
                        <input name="default_width" type="text" value="3"></input>
                        <input name="default_radius" type="text" value="7"></input>
                        <input name="keep_colors" type="checkbox" value="1"></input>
                        <input name="default_color" type="text" value="#aaaaaa"></input>
                        <input name="background_color" type="text" value="#ffffff"></input>
                        <input name="whole_pathways" type="checkbox" value="1"></input>
                        <input name="whole_modules" type="checkbox" value="1"></input>
                        <input name="query_reactions" type="checkbox" value="1"></input>
                        <input name="tax_filter" type="text" value=""></input>
                        <input name="map" type="radio" value="metabolic" checked></input>
                        <input name="map" type="radio" value="secondary"></input>
                        <input name="map" type="radio" value="microbial"></input>
                        <input name="map" type="radio" value="antibiotic"></input>
                        <input name="export_type" type="text" value="svg"></input>
                        <input name="export_dpi" type="text" value="120"></input>
                    </div>
                </form>
            </Item>
            <Title level={4}>Related KEGG Pathway</Title>
            <Title level={4}>Top 10 KEGG Pathways (ordered by p-value)</Title>
            <img
                src={
                    DATA_SERVICE_BASE_URL +
                    '/recipe_enrichment_image/' +
                    'rec_' +
                    result?.['detail']?.[0]['recipes_id'] +
                    '.png'
                }
                alt="Recipe enrichment image"
                style={{ width: '80%', marginTop: '10px', marginBottom: '3px' }}
            />
            <br />
            <br />
            <Title level={4}>Detailed Information of Enriched KEGG Pathways</Title>
            <Table columns={columnsPathways} dataSource={result?.related_pathways} style={{ marginBottom: '3vh' }} />
            <Title level={4}>Related Herbs Information</Title>
            <Table
                columns={columnsRelatedCDI}
                dataSource={result?.related_chinese_drug_info}
                style={{ marginBottom: '3vh' }}
            />

            <Title level={4}>
                Related Ingredients
                {/* {
                    <Button
                        style={{ float: 'right' }}
                        href={`${API_SERVICE_BASE_URL}/api/json_recipe_ingredients.php?recipes_id=${recipeId}`}
                        target="_blank"
                        download
                    >
                        Download this Table
                    </Button>
                } */}
            </Title>
            <Table columns={columnRi} dataSource={result?.related_ingredients} style={{ marginBottom: '3vh' }} />

            <Title level={4}>
                Related Targets
                {/* {
                    <Button
                        style={{ float: 'right' }}
                        href={`${API_SERVICE_BASE_URL}/api/json_recipe_ingredients_targets.php?recipes_id=${recipeId}`}
                        target="_blank"
                        download
                    >
                        Download this Table
                    </Button>
                } */}
            </Title>
            <Table
                columns={columnsIngredientTarget}
                dataSource={result?.ingredients_targets_association}
                style={{ marginBottom: '3vh' }}
            />

            <Title level={4}>Related diseases</Title>
            <Table
                columns={columnsDiseases}
                dataSource={result?.related_diseases}
                // onRow={(record) => ({
                //     onClick: () => {
                //         goToDiseaseDetail(record.id);
                //     },
                //     style: { cursor: 'pointer' },
                // })}
                style={{ marginBottom: '3vh' }}
            />
        </div>
    );
};

export default PageRecipeDetail;
